import {
  Breakpoint,
  useDeviceSize,
} from '@grubhubprod/umami-library/dist/hooks/useDeviceSize';
import { get } from '@grubhubprod/umami-library/dist/nodash/get';
import React, { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form/dist/index.ie11';
import { Transition, TransitionGroup } from 'react-transition-group';
import { LineOfCreditForm } from 'src/components/selfService/features/LineOfCredit/LineOfCreditForm';
import { ISignupStepProps } from 'src/components/common/features/Signup/components/SignupProgressTracker/SignupProgressTrackerStep/SignupProgressTrackerStep';
import {
  SignupStep,
  useSignupStatus,
} from 'src/components/selfService/features/Signup/context/SignupStatusProvider/SignupStatusProvider';
import { SignupStepFooter } from 'src/components/selfService/features/Signup/components/SignupStepFooter/SignupStepFooter';
import {
  ILocFormFields,
  mapToFormValues,
  mapToPayload,
} from 'src/models/LineOfCreditModelUtils';
import { pushErrorFromAPI } from 'src/utilities/notifications';
import { sdk } from 'src/utilities/sdk';
import { AsyncTaskStatus, useAsyncTask } from 'src/utilities/useAsyncTask';
import { getWindow } from 'src/utilities/window';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';
import { AnalyticsEventLabel } from 'src/types/Analytics';
import { useGetQueryParams } from 'src/utilities/useGetQueryParams';
import { OnboardingSection } from 'src/components/selfService/features/Signup/steps/LineOfCreditStep/OnboardingSection';
import { LineOfCreditStepPage } from 'src/components/selfService/features/Signup/steps/LineOfCreditStep/LineOfCreditStep.utils';
import {
  defaultFormValues,
  defaultDays,
  transitionDuration,
  defaultLineOfCreditStepStyles,
  LineOfCreditStepTransitionStyles,
} from 'src/components/selfService/features/Signup/steps/LineOfCreditStep/LineOfCreditStep.constants';
import { SignupStepFooterContent } from 'src/components/selfService/features/Signup/steps/LineOfCreditStep/SignupStepFooterContent';

export function LineOfCreditStep({
  updateMobileStepSize,
}: ISignupStepProps): JSX.Element {
  const { user } = useAuth();
  const formMethods = useForm<ILocFormFields>({
    mode: 'onChange',
    defaultValues: defaultFormValues,
  });
  const { handleSubmit } = formMethods;

  const {
    goToNextStep,
    goToPreviousStep,
    corpId,
    isStepCompleted,
    sendBackButtonAnalytics,
    isWelcomeBackModalPresent,
    isAdminIncludedInBudget,
  } = useSignupStatus();

  const isUpdating = useGetQueryParams('isUpdating');
  const [isUpdatingStep] = useState(isUpdating);
  const { breakpoint } = useDeviceSize(getWindow());
  const { startAsyncTask, status: submissionStatus } = useAsyncTask();

  const isCurrentStepCompleted = useRef(isStepCompleted(SignupStep.BUDGET));
  const [currentFormValues, setCurrentFormValues] =
    useState<ILocFormFields>(null);
  const [isExistingDataLoaded, setIsExistingDataLoaded] = useState(false);
  const defaultPage = isCurrentStepCompleted.current
    ? LineOfCreditStepPage.FORM
    : LineOfCreditStepPage.ONBOARDING;

  const [currentPage, setCurrentPage] = useState(defaultPage);
  const existingLocId = useRef(null);

  const isMobile = breakpoint < Breakpoint.sm;

  const { sendTealiumPageView, sendTealiumEvent } = useAnalytics();

  useEffect(() => {
    async function getLineOfCreditDetails() {
      try {
        const { results } = await sdk.http.get(
          `/corps/${corpId}/eventmetadata/search?filter_by=ONLY_ACTIVE`
        );

        if (get(results, 'length') > 0) {
          const { id } = results[0];

          existingLocId.current = id;

          setCurrentFormValues({
            ...mapToFormValues(results[0]),
            isAdminIncludedInBudget,
          });
        }
        setIsExistingDataLoaded(true);
      } catch (error) {
        pushErrorFromAPI(error);
      }
    }
    if (
      corpId &&
      isCurrentStepCompleted.current &&
      currentPage === LineOfCreditStepPage.FORM
    ) {
      getLineOfCreditDetails();
    }
  }, [currentPage, corpId, isAdminIncludedInBudget]);

  useEffect(() => {
    // send analytics on each page change between Onboard and Form
    if (!isWelcomeBackModalPresent) {
      sendTealiumPageView({
        pageGroup: 'corporate self service - sign up flow',
        pageName:
          currentPage === LineOfCreditStepPage.ONBOARDING
            ? 'line of credit - onboarding'
            : 'line of credit - creation',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, isWelcomeBackModalPresent]);

  useEffect(() => {
    if (updateMobileStepSize) {
      updateMobileStepSize();
    }
  }, []);

  useEffect(() => {
    if (submissionStatus === AsyncTaskStatus.COMPLETE) {
      goToNextStep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionStatus]);

  const isOnboardingPage = currentPage === LineOfCreditStepPage.ONBOARDING;

  function onContinueButtonClick(values: ILocFormFields): void {
    if (isOnboardingPage) {
      setCurrentPage(LineOfCreditStepPage.FORM);
    } else {
      submitForm(values);
    }
  }

  async function submitForm(values: ILocFormFields) {
    const budgetPayload = mapToPayload(values);
    try {
      if (existingLocId.current) {
        await startAsyncTask(() => {
          return sdk.http.put(
            `/corp-admin/${corpId}/budget/${existingLocId.current}`,
            budgetPayload
          );
        });
      } else {
        await startAsyncTask(() => {
          return sdk.http.post(`/corp-admin/${corpId}/budget`, budgetPayload);
        });
      }
      // Update basic account to indicate whether or not admin is included in budget
      await sdk.http.put(
        `/corp-admin/corp/self/management/${user.ud_id}/${corpId}`,
        {
          admin_included_in_budget_on_sign_up_complete:
            values.isAdminIncludedInBudget,
        }
      );
    } catch (error) {
      pushErrorFromAPI(error);
    }
  }

  function onSkip(): void {
    sendTealiumEvent({
      eventcategory: 'self-service sign up_line of credit',
      eventaction: 'line_of_credit_skip',
      eventlabel: `skip_${AnalyticsEventLabel.SUCCESS}`,
    });
    goToNextStep();
  }

  function onBackButtonClick(): void {
    sendBackButtonAnalytics();
    if (isOnboardingPage) {
      goToPreviousStep();
    } else {
      setCurrentPage(LineOfCreditStepPage.ONBOARDING);
    }
  }

  return (
    <FormProvider {...formMethods}>
      <div className="lineOfCreditStep_container">
        <TransitionGroup>
          {isOnboardingPage && (
            <Transition timeout={transitionDuration}>
              {(transitionState) => (
                <OnboardingSection
                  style={{
                    ...defaultLineOfCreditStepStyles,
                    ...LineOfCreditStepTransitionStyles[transitionState],
                  }}
                />
              )}
            </Transition>
          )}
          {!isOnboardingPage && (
            <Transition timeout={transitionDuration}>
              {(transitionState) => (
                <LineOfCreditForm
                  updateMobileStepSize={updateMobileStepSize}
                  isEditing={!!existingLocId.current}
                  useDatePickerModal={isMobile}
                  formMethods={formMethods}
                  isDataLoading={
                    isCurrentStepCompleted.current && !isExistingDataLoaded
                  }
                  values={currentFormValues}
                  defaultDays={defaultDays}
                  style={{
                    ...defaultLineOfCreditStepStyles,
                    ...LineOfCreditStepTransitionStyles[transitionState],
                  }}
                  isSignup
                />
              )}
            </Transition>
          )}
        </TransitionGroup>
      </div>
      <SignupStepFooter>
        <SignupStepFooterContent
          currentPage={currentPage}
          submissionStatus={submissionStatus}
          isUpdatingStep={!!isUpdatingStep}
          isOnboardingPage={isOnboardingPage}
          handleBack={onBackButtonClick}
          handleSkip={onSkip}
          handleSubmit={handleSubmit(onContinueButtonClick)}
        />
      </SignupStepFooter>
    </FormProvider>
  );
}
